import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import Typography from "@mui/material/Typography";

import type { TmsEvent } from "@storyboard/web-client";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import {
  type CallRejectionDetail,
  type CallTrailerDetail,
  tmsEventDetailOf,
} from "@/components/feed/tmsEvent/tmsEventDetail";
import { CHIP_SEPARATOR } from "@/components/feed/tmsEvent/tmsEventDisplay";

/**
 * The TMS's status line above the driver's call as it was typed, monospaced and unwrapped so the call's 40-column
 * layout holds.
 */
export function CallRejectionDetailContent({ detail }: { detail: CallRejectionDetail }) {
  return (
    <Box data-sb={TestIds.feed.tmsEventDetail} sx={{ p: 1.5 }}>
      <Typography variant="body2">
        {[detail.statusCode, detail.statusDesc, detail.statusMsg].join(CHIP_SEPARATOR)}
      </Typography>
      {detail.callLines.length > 0 && (
        <Box
          component="pre"
          sx={{
            m: 0,
            mt: 1,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: "0.75rem",
            whiteSpace: "pre",
          }}
        >
          {detail.callLines.join("\n")}
        </Box>
      )}
    </Box>
  );
}

/** How the trailer changed hands, in words; null when the call did not say. */
function exchangeLabel({ kind, trailerExchange }: CallTrailerDetail): string | null {
  switch (trailerExchange) {
    case "swapped":
      return "Drop and hook";
    case "droppedOnly":
      return "Dropped, left bobtail";
    case "kept":
      return kind === "emptyCall" ? "Live unload" : "No trailer dropped";
    default:
      return null;
  }
}

/** The popover's labeled rows, a dash for a number the driver left blank. */
function callTrailerRows(detail: CallTrailerDetail): [label: string, value: string][] {
  const { stopName, city, trailerExchange, droppedTrailerNumber, departingTrailerNumber } = detail;
  const rows: [string, string][] = [["Stop", city ? `${stopName} — ${city}` : stopName]];

  const exchange = exchangeLabel(detail);
  if (exchange) rows.push(["Trailer", exchange]);

  if (trailerExchange === "swapped" || trailerExchange === "droppedOnly") {
    rows.push(["Dropped", droppedTrailerNumber ?? "—"]);
  }

  rows.push(["Left with", trailerExchange === "droppedOnly" ? "bobtail" : (departingTrailerNumber ?? "—")]);
  return rows;
}

/** A version 2 call's stop and both sides of its trailer exchange, one labeled row each. */
export function CallTrailerDetailContent({ detail }: { detail: CallTrailerDetail }) {
  return (
    <Box
      data-sb={TestIds.feed.tmsEventDetail}
      sx={{ p: 1.5, display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 1.5, rowGap: 0.5 }}
    >
      {callTrailerRows(detail).map(([label, value]) => (
        <Box key={label} sx={{ display: "contents" }}>
          <Typography variant="body2" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="body2">{value}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/**
 * What a TMS event chip opens on click, anchored to it; nothing for an event with no detail.
 *
 * Clicks inside stop here: React bubbles them out of the portal to the chip's own ancestors, which would otherwise
 * select the row under it.
 */
export default function TmsEventDetailPopover({
  event,
  anchorEl,
  onClose,
}: {
  event: TmsEvent;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}) {
  const detail = tmsEventDetailOf(event);
  if (!detail) return null;
  return (
    <Popover
      open={anchorEl != null}
      anchorEl={anchorEl}
      onClose={onClose}
      onClick={(click) => click.stopPropagation()}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      {detail.type === "callRejection" ? (
        <CallRejectionDetailContent detail={detail.detail} />
      ) : (
        <CallTrailerDetailContent detail={detail.detail} />
      )}
    </Popover>
  );
}
